import { Routes } from '@angular/router';
import { HomeComponent } from './components/home/home.component';
import { MembersComponent } from './components/members/members.component';
import { PaymentsComponent } from './components/payments/payments.component';
import { ReportsComponent } from './components/reports/reports.component';
import { SettingsComponent } from './components/settings/settings.component';
import { LoginComponent } from './components/auth/login/login.component';
import { OAuthCallbackComponent } from './components/auth/oauth-callback/oauth-callback.component';
import { AcceptInvitationComponent } from './components/auth/accept-invitation/accept-invitation.component';
import { TenantSetupComponent } from './components/setup/tenant-setup.component';
import { BusinessUnitSetupComponent } from './components/setup/business-unit-setup.component';
import { MainLayoutComponent } from './layouts/main-layout.component';
import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { tenantSetupGuard } from './core/guards/tenant-setup.guard';
import { businessUnitSetupGuard } from './core/guards/business-unit-setup.guard';
import { adminGuard, superAdminGuard } from './core/guards/admin.guard';
import { subscriptionReadOnlyGuard } from './core/guards/subscription-read-only.guard';
import { vaultAuthGuard } from './core/guards/vault-auth.guard';

export const routes: Routes = [
  // The family vault is the product now; the committee module is legacy and stays reachable at
  // /home for as long as it exists.
  { path: '', redirectTo: '/vault', pathMatch: 'full' },

  // ---- Family vault -------------------------------------------------------
  // Entirely separate from the committee routes above: its own sign-in, its own session, its own
  // guard. Nothing is shared, so removing the committee module later means deleting the other
  // routes and changing this redirect — not untangling them.
  {
    path: 'vault/sign-in',
    title: 'Sign in — Family Vault',
    loadComponent: () => import('./components/vault/sign-in/sign-in.component')
      .then(m => m.VaultSignInComponent),
  },
  {
    path: 'vault',
    loadComponent: () => import('./components/vault/vault-shell.component')
      .then(m => m.VaultShellComponent),
    canActivate: [vaultAuthGuard],
    children: [
      {
        path: '',
        title: 'Family Tree — Family Vault',
        loadComponent: () => import('./components/vault/family-tree/family-tree.component')
          .then(m => m.FamilyTreeComponent),
      },
      {
        path: 'people',
        title: 'People — Family Vault',
        loadComponent: () => import('./components/vault/person-select/person-select.component')
          .then(m => m.PersonSelectComponent),
      },
      {
        path: 'persons/:personId',
        title: 'Documents — Family Vault',
        loadComponent: () => import('./components/vault/person-categories/person-categories.component')
          .then(m => m.PersonCategoriesComponent),
      },
      {
        path: 'persons/:personId/categories/:categoryId',
        title: 'Documents — Family Vault',
        loadComponent: () => import('./components/vault/category-documents/category-documents.component')
          .then(m => m.CategoryDocumentsComponent),
      },
      {
        path: 'settings',
        title: 'Settings — Family Vault',
        loadComponent: () => import('./components/vault/settings/vault-settings.component')
          .then(m => m.VaultSettingsComponent),
      },
    ],
  },

  { path: 'login', component: LoginComponent, title: 'Login' },
  { path: 'accept-invitation', component: AcceptInvitationComponent, title: 'Accept Invitation' },
  { path: 'auth/oauth/callback', component: OAuthCallbackComponent, title: 'Signing In' },
  {
    path: 'change-password',
    title: 'Change Password',
    loadComponent: () => import('./components/auth/change-password/change-password.component')
      .then(m => m.ChangePasswordComponent),
    canActivate: [authGuard]
  },
  {
    path: 'setup/tenant',
    component: TenantSetupComponent,
    title: 'Tenant Setup',
    canActivate: [authGuard, tenantSetupGuard]
  },
  {
    path: 'setup/business-unit',
    component: BusinessUnitSetupComponent,
    title: 'Business Unit Setup',
    canActivate: [authGuard, tenantSetupGuard, businessUnitSetupGuard]
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard, tenantSetupGuard, businessUnitSetupGuard],
    canActivateChild: [subscriptionReadOnlyGuard],
    children: [
      { path: 'home', component: HomeComponent, title: 'Dashboard' },
      {
        path: 'members',
        component: MembersComponent,
        title: 'Members',
        canActivate: [permissionGuard],
        data: { permissions: [201, 202] }
      },
      {
        path: 'members/:id',
        title: 'Member Details',
        loadComponent: () => import('./components/members/member-detail/member-detail.component')
          .then(m => m.MemberDetailComponent),
        canActivate: [permissionGuard],
        // allowSelf: a member with none of the Member permissions can still open their
        // own record (read-only) — see permissionGuard.
        data: { permissions: [201, 202], allowSelf: true }
      },
      {
        path: 'events',
        title: 'Events',
        loadComponent: () => import('./components/events/events-list/events-list.component')
          .then(m => m.EventsListComponent),
      },
      {
        path: 'events/:id',
        title: 'Event Details',
        loadComponent: () => import('./components/events/event-detail/event-detail.component')
          .then(m => m.EventDetailComponent),
      },
      {
        path: 'payments',
        component: PaymentsComponent,
        title: 'Payments',
        canActivate: [permissionGuard],
        data: { permissions: [301, 302] }
      },
      {
        path: 'payments/:id',
        title: 'Payment Details',
        loadComponent: () => import('./components/payments/payment-detail/payment-detail.component')
          .then(m => m.PaymentDetailComponent),
        canActivate: [permissionGuard],
        data: { permissions: [301, 302] }
      },
      {
        path: 'ledger',
        title: 'Ledger',
        loadComponent: () => import('./components/ledger/ledger-list/ledger-list.component')
          .then(m => m.LedgerListComponent),
        canActivate: [permissionGuard],
        data: { permissions: [401] }
      },
      {
        path: 'ledger/summary',
        title: 'Ledger Summary',
        loadComponent: () => import('./components/ledger/ledger-summary/ledger-summary.component')
          .then(m => m.LedgerSummaryComponent),
        canActivate: [permissionGuard],
        data: { permissions: [401] }
      },
      {
        path: 'ledger/categories',
        title: 'Categories',
        loadComponent: () => import('./components/ledger/category-manager/category-manager.component')
          .then(m => m.CategoryManagerComponent),
        canActivate: [permissionGuard],
        data: { permissions: [416] }
      },
      {
        path: 'expenses',
        title: 'Expenses',
        loadComponent: () => import('./components/expenses/expense-list/expense-list.component')
          .then(m => m.ExpenseListComponent),
        canActivate: [permissionGuard],
        data: { permissions: [411] }
      },
      {
        path: 'reports',
        component: ReportsComponent,
        title: 'Reports',
        canActivate: [permissionGuard],
        data: { permissions: [501] }
      },
      { path: 'settings', component: SettingsComponent, title: 'Settings' },
      {
        path: 'account/profile',
        title: 'My Profile',
        loadComponent: () => import('./components/account/my-profile.component')
          .then(m => m.MyProfileComponent)
      },
      {
        path: 'admin/member-change-requests',
        title: 'Member Change Requests',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/admin/member-change-requests/member-change-requests-list.component')
          .then(m => m.MemberChangeRequestsListComponent)
      },
      {
        path: 'admin/tenants',
        title: 'Tenants',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./components/admin/tenants/tenant-list.component')
          .then(m => m.TenantListComponent)
      },
      {
        path: 'admin/tenants/:tenantId',
        title: 'Tenant Details',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./components/admin/tenants/tenant-detail.component')
          .then(m => m.TenantDetailComponent)
      },
      {
        path: 'admin/business-units',
        title: 'Business Units',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/admin/business-units/business-unit-list.component')
          .then(m => m.BusinessUnitListComponent)
      },
      {
        path: 'admin/custom-fields',
        title: 'Custom Fields',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/admin/custom-fields/custom-fields-admin.component')
          .then(m => m.CustomFieldsAdminComponent)
      },
      // Legacy path — keep links working after the rename.
      { path: 'admin/business-unit-fields', redirectTo: 'admin/custom-fields', pathMatch: 'full' },
      {
        path: 'admin/subscription-plans',
        title: 'Subscription Plans',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./components/admin/subscription-plans/subscription-plan-list.component')
          .then(m => m.SubscriptionPlanListComponent)
      },
      {
        path: 'admin/subscriptions/queue',
        title: 'Subscription Queue',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./components/admin/subscription-queue/subscription-queue.component')
          .then(m => m.SubscriptionQueueComponent)
      },
      {
        path: 'billing',
        title: 'Billing',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/billing-dashboard.component')
          .then(m => m.BillingDashboardComponent)
      },
      {
        path: 'billing/invoices',
        title: 'Invoices',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/invoice-list.component')
          .then(m => m.InvoiceListComponent)
      },
      {
        path: 'billing/invoices/:id',
        title: 'Invoice Details',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/invoice-detail.component')
          .then(m => m.InvoiceDetailComponent)
      },
      {
        path: 'billing/change-plan',
        title: 'Change Plan',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./components/billing/change-plan.component')
          .then(m => m.ChangePlanComponent)
      },
      {
        path: 'billing/submit-payment',
        title: 'Submit Payment',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/submit-payment.component')
          .then(m => m.SubmitPaymentComponent)
      },
      {
        path: 'billing/submissions',
        title: 'My Submissions',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/my-submissions.component')
          .then(m => m.MySubmissionsComponent)
      },
      {
        path: 'billing/request-exemption',
        title: 'Request Exemption',
        canActivate: [adminGuard],
        loadComponent: () => import('./components/billing/request-exemption.component')
          .then(m => m.RequestExemptionComponent)
      }
    ]
  },
  { path: '**', redirectTo: '/home' }
];
